import {
  computeE1rmKg,
  dropsetSetTotals,
  fromCanonicalKg,
  toCanonicalKg,
  type DropsetVariant,
  type Exercise,
} from '@jackedlogs/shared';

import { type DraftEntry, type DraftSet, rowIdOf } from './state/loggerDraftTypes';
import { parseLocaleNumber } from './parseLocaleNumber';
import { draftDropsetRounds } from './dropsetDraft';
import {
  bodyweightPerRepLoad,
  displayLoad,
  rawRoundsForSet,
  toDropsetRound,
} from './dropsetRoundMap';

// Pure row-builder for the timeline-logger feed (Phase 2, slice 2). Flattens
// the nested draft into a CHRONOLOGICAL list of COMPLETED set events — the
// timeline holds only real events (next/timeline-logger.md §3.1: planned /
// pending sets are not timeline rows; not-done is never a fail). Phase-1 stores
// in source order; the feed wants performed order, so we sort by completedAt
// ascending (oldest first), tiebroken by source order for stability (two sets
// stamped in the same ms keep their logged sequence, earliest-logged on top).
//
// ORDER: oldest at the top, newest at the bottom (Antenore 2026-06-01, inbox
// 11f01866 — "in alto il primo eseguito, in fondo l'ultimo"). The "workout
// started" marker therefore renders ABOVE the first row (see TimelineList).
//
// exerciseName is the snapshot fallback only — the renderer resolves the live
// catalog name via getExerciseNameBySlug ([[feedback_no_denormalized_display_snapshots]]).

export type TimelineRowKind = 'weighted' | 'bodyweight' | 'cardio' | 'timed';

export interface TimelineRow {
  key: string;
  /** The row this set belongs to (LG-39). Tapping a set in the feed refocuses
   *  THAT row — with the same exercise logged three times in a workout, the
   *  slug below cannot say which of the three was tapped. */
  entryId: string;
  exerciseId: string;
  exerciseName: string;
  kind: TimelineRowKind;
  /** Superset/circuit group id when the owning entry is grouped, else null.
   *  Drives the connector band; consecutive same-id rows read as one block. */
  groupId: string | null;
  groupKind: 'superset' | 'circuit' | null;
  completedAt: string;
  reps: string;
  weight: string;
  unit: 'kg' | 'lb';
  /** Laterality multiplier stamped on the set (1 = bilateral/total entered,
   *  2 = one side/implement entered, both counted). Drives the «2×» prefix
   *  on the weight so the per-implement convention is readable in the feed
   *  itself, not only in the volume chip (IB-8 family, 2026-07-14). */
  loadedSides: 1 | 2;
  extraNegative: boolean;
  duration: string;
  distanceM: string;
  fail: boolean;
  pr: boolean;
  /** Bidirectional MAX (next/coaching.md §9): the DOWNWARD twin of `pr`.
   *  True when this set is a qualifying MAX-regression signal → the row
   *  shows a red ⬇️, mirror of the 🏆. UI-only, derived from the draft's
   *  regressionFlash. */
  regression: boolean;
  /** Epley estimated 1RM in kg for this set (slice 5c), or null when it
   *  doesn't qualify (non-weighted, failed, reps outside 1..12, no weight,
   *  unparseable input). The renderer formats it in the row's display unit
   *  and compares it against the exercise's recent-history best. */
  e1rmKg: number | null;
  /** Set tonnage in the row's DISPLAY unit (weight × reps × loadedSides),
   *  or null for non-weighted / unparseable sets. Surfaced on the row so the
   *  athlete can SEE how the app interpreted the load — in particular the ×2
   *  on unilateral / two-implement work, which was previously invisible
   *  (Antenore 2026-06-19: "loggando un set non si vede quanto volume si è
   *  fatto … la mini card dovrebbe mostrare il volume del set singolo").
   *  Display unit (not canonical kg) so it sanity-checks against the number
   *  the athlete actually typed. Bodyweight sets included since IB-9
   *  (bw×pct/100 + extra), matching what the server already counted. */
  volume: number | null;
  /** Bodyweight rows only: the per-rep load actually moved, in the row's
   *  display unit (bw×pct/100 + signed extra). Null for every other kind, and
   *  for a bodyweight set the profile can't price (no bodyweight on file, no
   *  extra). The summary shows it in place of the raw `weight` field, which on
   *  a bodyweight entry holds only the EXTRA — blank on a plain push-up, which
   *  is why the feed read "10 reps" and nothing else (IB-9). */
  bodyweightLoad: number | null;
  /** Dropset/myorep technique of this set, or null for a plain set. Drives
   *  the row's technique badge (never colour-only — the label differs) and
   *  the =top weight-inheritance folded into `volume`. IB-1: a myorep's work
   *  was invisible in the feed — the row read only the (blank) parent. */
  dropsetVariant: DropsetVariant | null;
  /** The chain steps AFTER the top round (the top shows on the reps/weight
   *  line), for the cluster breakdown (e.g. 10·9·5·4·4). Null for a plain
   *  set. Myorep steps repeat the top load (blank weight = =top); classical
   *  steps carry their own dropped weight. */
  dropRounds: readonly { reps: string; weight: string }[] | null;
}

// Epley e1RM for a logged set, normalised to kg. Only weighted sets qualify;
// reps/weight are draft strings (locale-typed) so we parse + canonicalise the
// unit before handing off to the shared computeE1rmKg (which owns the
// eligibility rules: weighted, 1 ≤ reps ≤ 12, weight > 0 — a fail is NOT
// excluded, AP-0p model).
function rowE1rmKg(kind: TimelineRowKind, set: DraftSet): number | null {
  if (kind !== 'weighted') return null;
  // e1RM is a TOP-round concern only (dropsetTotals.ts header). For a chain
  // the top lives in draftDropsetRounds()[0] — the new-model parent reps/weight
  // are a blank summary, so reading them directly would drop the e1RM. A plain
  // set has no chain and reads its own row.
  const top = set.dropset
    ? draftDropsetRounds(set)[0]
    : { reps: set.reps, weight: set.weight };
  if (!top) return null;
  const reps = parseLocaleNumber(top.reps);
  const weight = parseLocaleNumber(top.weight);
  if (!Number.isFinite(reps) || !Number.isFinite(weight)) return null;
  return computeE1rmKg({
    kind: 'weighted',
    reps,
    weight: toCanonicalKg(weight, set.unit),
  });
}

/** The bodyweight facts a row needs to price its sets, resolved once per
 *  entry from the catalog + profile. `bodyweightKg` is canonical; `pct` is the
 *  exercise's bodyweightLoadPct (100 when the catalog omits it, matching the
 *  server's `setVolumeKg`). */
type BodyweightCtx = { bodyweightKg: number; pct: number };

// Display-unit rounds for a logged set — plain OR chained, weighted OR
// bodyweight. The chip is DISPLAY-unit (a sanity-check against the number the
// athlete typed, Antenore 2026-06-19), so — unlike the footer's canonical-kg
// totals — we do NOT canonicalise; the magnitude entered IS the value summed.
// The mapper itself is shared with the footer (dropsetRoundMap) so the two can
// never disagree on how a round reads; only the load transform differs.
function displayRounds(set: DraftSet, isBodyweight: boolean) {
  return rawRoundsForSet(set).map((r) =>
    toDropsetRound(r, isBodyweight, set.loadedSides, displayLoad),
  );
}

// Display-unit tonnage for a logged set, in the unit the weight was entered.
// Reuses the shared dropsetSetTotals so the row can never disagree with the
// footer or the server on WHICH rounds count (top + steps, myorep =top, once)
// or on the ×2 laterality rule — only the unit differs.
//
// Bodyweight sets price at (bw×pct/100 + extra) × reps × sides, the same
// formula the server already used for the SESSION_RESULT — IB-9: a push-up
// showed no tonnage at all in the feed while the server counted it, so the
// athlete saw a zero-volume set that wasn't. Display-only: no stored number
// changes, hence no heal.
export function rowVolume(
  kind: TimelineRowKind,
  set: DraftSet,
  bw: BodyweightCtx,
): number | null {
  if (kind !== 'weighted' && kind !== 'bodyweight') return null;
  // `reps` is the clean count (AP-0p model: fail is a pure label), so a
  // failed set's row volume counts it like any other.
  const { volumeKg } = dropsetSetTotals(
    set.dropset?.variant ?? 'classical',
    displayRounds(set, kind === 'bodyweight'),
    {
      bodyweightKg: fromCanonicalKg(bw.bodyweightKg, set.unit),
      bodyweightLoadPct: bw.pct,
    },
  );
  return volumeKg > 0 ? volumeKg : null;
}

// Per-rep effective load of a BODYWEIGHT set in the row's display unit, so the
// feed shows what a push-up actually moves instead of a blank (IB-9). Mirrors
// the footer's weight column (totalsMath weightSumKg) via the same shared
// helper — the two columns describe the same set and must not disagree.
function rowBodyweightLoad(
  kind: TimelineRowKind,
  set: DraftSet,
  bw: BodyweightCtx,
): number | null {
  if (kind !== 'bodyweight') return null;
  const load = bodyweightPerRepLoad(
    set,
    fromCanonicalKg(bw.bodyweightKg, set.unit),
    bw.pct,
    displayLoad,
  );
  // Only an exact 0 means there is nothing to say (no bodyweight on file and
  // no extra typed). A NEGATIVE load is real information — a band-assisted
  // pull-up logged by an athlete with no profile bodyweight nets below zero —
  // and the footer prints it signed (Totals.tsx `weightSumKg !== 0`), so
  // hiding it here would re-open IB-9 for exactly that set while the two
  // surfaces disagree about the same work.
  return load !== 0 ? load : null;
}

// `exercises` + `bodyweightKg` are REQUIRED, not defaulted: a bodyweight row
// prices its load from the catalog pct and the profile's weight, and a silent
// default would reintroduce IB-9 as a zero instead of a type error at the one
// callsite (useTimelineLogger, which already holds both).
export function buildTimelineRows(
  entries: readonly DraftEntry[],
  exercises: readonly Exercise[] | null,
  bodyweightKg: number,
): TimelineRow[] {
  const rows: { row: TimelineRow; seq: number }[] = [];
  let seq = 0;
  entries.forEach((entry, entryIdx) => {
    const kind: TimelineRowKind = entry.isTimeBased
      ? 'timed'
      : entry.isCardio
        ? 'cardio'
        : entry.isBodyweight
          ? 'bodyweight'
          : 'weighted';
    // Resolved by slug, never read off a snapshot on the entry
    // ([[feedback_no_denormalized_display_snapshots]]). Absent pct → 100, the
    // same fallback the server's setVolumeKg applies.
    const exercise = exercises?.find((c) => c.slug === entry.exerciseId);
    const bw: BodyweightCtx = {
      bodyweightKg,
      pct:
        entry.isBodyweight && exercise?.bodyweightLoadPct !== undefined
          ? exercise.bodyweightLoadPct
          : 100,
    };
    entry.sets.forEach((set, setIdx) => {
      const order = seq++;
      if (set.completedAt === null) return; // pending/planned: not a timeline event
      // For a chain the top round leads the reps/weight line (the new-model
      // parent reps/weight are a blank summary), and the remaining steps become
      // the cluster breakdown. A plain set has no chain → shows its own fields.
      const chain = set.dropset ? draftDropsetRounds(set) : null;
      const top = chain && chain.length > 0 ? chain[0]! : null;
      const isMyorep = set.dropset?.variant === 'myorep';
      // A myorep's clusters share the top load (=top), so the SET's rep count
      // is the TOTAL across activation + clusters — the top-only count read as
      // "20 reps" for a 52-rep set (Antenore 2026-07-03). Classical drops
      // change the weight, so the top set leads the line and the drops sit in
      // the breakdown with their own loads. Reuse the shared totals helper for
      // the sum so the count can't diverge from the volume.
      // `set.reps` is already the clean count on a fail (AP-0p model: the
      // failed rep was decremented at log time), so it displays directly.
      const displayReps = isMyorep
        ? String(
            dropsetSetTotals(
              'myorep',
              displayRounds(set, kind === 'bodyweight'),
            ).reps,
          )
        : top
          ? top.reps
          : set.reps;
      rows.push({
        seq: order,
        row: {
          key: `${entryIdx}-${setIdx}-${set.completedAt}`,
          entryId: rowIdOf(entry),
          exerciseId: entry.exerciseId,
          exerciseName: entry.exerciseName,
          kind,
          groupId: entry.group?.id ?? null,
          groupKind: entry.group?.kind ?? null,
          completedAt: set.completedAt,
          reps: displayReps,
          weight: top ? top.weight : set.weight,
          unit: set.unit,
          loadedSides: set.loadedSides,
          extraNegative: set.extraNegative,
          duration: set.duration,
          distanceM: set.distanceM,
          fail: set.fail,
          pr: set.prFlash,
          regression: set.regressionFlash ?? false,
          e1rmKg: rowE1rmKg(kind, set),
          volume: rowVolume(kind, set, bw),
          bodyweightLoad: rowBodyweightLoad(kind, set, bw),
          dropsetVariant: set.dropset ? set.dropset.variant : null,
          // Myorep breakdown includes the activation round so it sums to the
          // headline rep count (20 · 10 · 9 · 5 · 4 · 4 = 52); classical shows
          // only the drops (its top set is already the main line).
          dropRounds: chain
            ? isMyorep
              ? chain.map((r) => ({ reps: r.reps, weight: r.weight }))
              : chain.length > 1
                ? chain.slice(1).map((r) => ({ reps: r.reps, weight: r.weight }))
                : null
            : null,
        },
      });
    });
  });
  // Oldest first; stable tiebreak on source order so same-ms stamps keep
  // their logged sequence (ascending seq → the earlier-logged shows on top).
  rows.sort((a, b) => {
    if (a.row.completedAt < b.row.completedAt) return -1;
    if (a.row.completedAt > b.row.completedAt) return 1;
    return a.seq - b.seq;
  });
  return rows.map((r) => r.row);
}
